import { isProposalDeliveryComplete } from '@bcube/shared/constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { proposalApi } from '@/api/commercial/proposals';
import { StepNavigation } from '@/components/commercial/accounts/create/step-navigation';
import { StepConfigurations } from '@/components/commercial/proposals/create/configurations/step-configurations';
import { ProposalStatusBadge } from '@/components/commercial/proposals/create/proposal-status-badge';
import { ResumeHeaderActions } from '@/components/commercial/proposals/create/resume/resume-actions-panel';
import { StepCart } from '@/components/commercial/proposals/create/step-cart';
import {
  StepCartFooterActions,
  StepCartHeaderActions,
} from '@/components/commercial/proposals/create/step-cart-actions';
import { StepClientContainer } from '@/components/commercial/proposals/create/step-client-container';
import { StepCreditAnalysis } from '@/components/commercial/proposals/create/step-credit-analysis';
import { StepDelivery } from '@/components/commercial/proposals/create/step-delivery';
import { StepResume } from '@/components/commercial/proposals/create/step-resume';
import { CreateProposalProvider, useCreateProposal } from '@/context/create-proposal-context';
import {
  type PaymentFormState,
  useCartStepValidation,
} from '@/hooks/commercial/use-cart-step-validation';
import { useProposalConfigurations } from '@/hooks/commercial/use-proposal-configurations';
import { useProposalDeliveryGuard } from '@/hooks/commercial/use-proposal-delivery-guard';
import { useProposalStepProgress } from '@/hooks/use-proposal-step-progress';
import { toast } from '@/hooks/use-toast';

const STEP_IDS = ['client', 'credit-analysis', 'cart', 'config', 'delivery', 'resume'] as const;

function ProposalCreateContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = searchParams.get('step') || 'client';

  const { proposal } = useCreateProposal();
  const [isDeliverySaving, setIsDeliverySaving] = useState(false);
  const [draft, setDraft] = useState<{ proposalId?: string; payment: PaymentFormState }>();
  const [isSaving, setIsSaving] = useState(false);

  const savePaymentFnRef = useRef<(() => Promise<void>) | null>(null);
  const stepNavigationInFlightRef = useRef(false);
  const isCartStep = currentStep === 'cart';
  const isResumeStep = currentStep === 'resume';

  const proposalId = proposal?.id;
  const cartValidation = useCartStepValidation({
    proposalId,
    draftPayment: isCartStep && draft?.proposalId === proposalId ? draft?.payment : undefined,
  });
  const handlePaymentStateChange = useCallback(
    (payment: PaymentFormState | undefined) => {
      setDraft(payment ? { proposalId, payment } : undefined);
    },
    [proposalId],
  );

  const { data: remoteProposal } = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () => proposalApi.findOne(proposalId!),
    enabled: !!proposalId,
  });
  const { data: proposalConfigurations } = useProposalConfigurations(proposalId);

  const isConfigurationStepValid = proposalConfigurations?.isValidated ?? false;
  const deliveryProposal = remoteProposal;
  const isDeliveryComplete = isProposalDeliveryComplete(deliveryProposal);
  const redirectDeliveryStep = useCallback(
    (step: string) => setSearchParams({ step }, { replace: true }),
    [setSearchParams],
  );
  const deliveryGuard = useProposalDeliveryGuard({
    currentStep,
    proposalId,
    isCartValid: cartValidation.isValid,
    isConfigurationValid: isConfigurationStepValid,
    isDeliveryComplete,
    isLoading: cartValidation.isLoading || (!!proposalId && !remoteProposal),
    redirect: redirectDeliveryStep,
  });

  const steps = useProposalStepProgress({
    proposal,
    isDeliveryComplete,
    isCartStepValid: cartValidation.isValid,
    isConfigurationStepValid,
  });

  const handleSavePaymentRef = useCallback((saveFn: (() => Promise<void>) | null) => {
    savePaymentFnRef.current = saveFn;
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      if (proposalId) {
        await queryClient.invalidateQueries({
          queryKey: ['cart-calculate', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal-configurations', proposalId],
        });
      }
      toast({ variant: 'success', title: 'Proposta salva com sucesso' });
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  }, [proposalId, queryClient]);

  const handleNextStep = useCallback(async () => {
    if (stepNavigationInFlightRef.current || isDeliverySaving) return;
    stepNavigationInFlightRef.current = true;
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      if (proposalId) {
        await queryClient.invalidateQueries({
          queryKey: ['cart-calculate', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal-configurations', proposalId],
        });
      }
      const currentIdx = STEP_IDS.indexOf(currentStep as (typeof STEP_IDS)[number]);
      const nextStep = STEP_IDS[currentIdx + 1];
      if (nextStep) {
        setSearchParams({ step: nextStep }, { replace: true });
      }
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
    } finally {
      stepNavigationInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [currentStep, proposalId, queryClient, setSearchParams, isDeliverySaving]);

  const handleStepNavigate = useCallback(async () => {
    if (stepNavigationInFlightRef.current || isDeliverySaving) return false;
    if (currentStep !== 'cart') return true;

    stepNavigationInFlightRef.current = true;
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      return true;
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
      return false;
    } finally {
      stepNavigationInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [currentStep, isDeliverySaving]);

  const handleCancel = useCallback(() => {
    navigate('/commercial/proposal');
  }, [navigate]);

  const handleResumeUpdate = useCallback(() => {
    if (proposal?.id) {
      queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id] });
    }
  }, [proposal?.id, queryClient]);

  const handleReopenProposal = useCallback(() => {
    if (proposal?.id) {
      navigate(`/commercial/proposal/form/update/${proposal.id}`);
    }
  }, [proposal?.id, navigate]);

  const resumeTitle = remoteProposal
    ? `Proposta ${remoteProposal.seq} - ${remoteProposal.clientName || 'Cliente'}`
    : 'Nova Proposta';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div
        className={`flex justify-between gap-4 ${isCartStep ? 'flex-nowrap items-start' : 'flex-wrap items-center'}`}
      >
        <div className="min-w-0 flex-1 break-words">
          {isResumeStep ? (
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold">{resumeTitle}</h1>
              <ProposalStatusBadge status={remoteProposal?.status ?? proposal?.status} />
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold">Nova Proposta</h1>
              <p className="text-muted-foreground">
                Preencha os dados abaixo para criar uma nova proposta comercial
              </p>
            </>
          )}
        </div>
        {isCartStep && (
          <StepCartHeaderActions onCancel={handleCancel} onSave={handleSave} isSaving={isSaving} />
        )}
        {isResumeStep && deliveryGuard && (
          <ResumeHeaderActions
            proposalId={proposal?.id}
            status={remoteProposal?.status ?? proposal?.status}
            sentAt={remoteProposal?.sentAt}
            onSave={handleSave}
            isSaving={isSaving}
            onUpdate={handleResumeUpdate}
            onReopen={handleReopenProposal}
          />
        )}
      </div>

      {/* Step Navigation */}
      <div className="w-full max-w-[1400px] mx-auto mb-6">
        <StepNavigation
          steps={steps}
          onStepNavigate={handleStepNavigate}
          isNavigating={isSaving || isDeliverySaving}
        />
      </div>

      {/* Step Content */}
      <div className="w-full max-w-[1400px] mx-auto">
        {currentStep === 'client' && <StepClientContainer />}
        {currentStep === 'credit-analysis' && <StepCreditAnalysis />}
        {isCartStep && (
          <>
            <StepCart
              isPickup={deliveryProposal?.deliveryMode === 'BWS'}
              proposalId={proposal?.id}
              clientId={proposal?.clientId}
              savedPayment={remoteProposal?.payment}
              onPaymentStateChange={handlePaymentStateChange}
              onSavePaymentRef={handleSavePaymentRef}
            />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={cartValidation.isValid}
              validationHint={cartValidation.message}
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'config' && (
          <>
            <StepConfigurations proposalId={proposal?.id} />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={!!proposalId && cartValidation.isValid}
              validationHint={cartValidation.message}
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'delivery' && deliveryGuard && (
          <>
            <StepDelivery
              accountId={proposal?.clientId}
              proposalId={proposal?.id}
              proposal={deliveryProposal}
              onSavingChange={setIsDeliverySaving}
            />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={isDeliveryComplete && !isDeliverySaving}
              validationHint={isDeliveryComplete ? null : 'Selecione o destino da proposta'}
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'resume' && deliveryGuard && (
          <StepResume
            proposalId={proposal?.id}
            clientId={proposal?.clientId}
            onUpdate={handleResumeUpdate}
          />
        )}
      </div>
    </motion.div>
  );
}

export function ProposalCreatePage() {
  return (
    <CreateProposalProvider resetOnMount>
      <ProposalCreateContent />
    </CreateProposalProvider>
  );
}
